import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { env } from './env';

// Single Application Insights instance for the whole app. Points at the same
// App Insights resource as the .NET backend so frontend ↔ backend traces
// correlate via the W3C traceparent header.
//
// When VITE_APPINSIGHTS_CONNECTION_STRING is empty (typical in local dev),
// the SDK is initialized in a no-op state so calls don't throw.

export const reactPlugin = new ReactPlugin();

let appInsights: ApplicationInsights | null = null;
let initialized = false;

export function initTelemetry(): void {
  if (initialized) return;
  initialized = true;

  if (!env.appInsightsConnectionString) {
    if (env.isDev) {
      // eslint-disable-next-line no-console
      console.info('[telemetry] App Insights connection string not set — telemetry disabled.');
    }
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString: env.appInsightsConnectionString,
      extensions: [reactPlugin],
      enableAutoRouteTracking: true,            // SPA page-view events on route change
      enableCorsCorrelation: true,              // emit traceparent on cross-origin XHR
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      autoTrackPageVisitTime: true,
      disableTelemetry: false,
      enableUnhandledPromiseRejectionTracking: true,
      // The list of domains where traceparent should be added. Required for
      // correlation with the .NET backend on a different origin.
      correlationHeaderDomains: deriveCorrelationDomains(env.apiUrl),
      // Tag every event with our app name + env so dashboards can split by app.
      // (cloud_RoleName / cloud_RoleInstance use telemetry initializers below.)
    },
  });

  appInsights.loadAppInsights();

  appInsights.addTelemetryInitializer((envelope) => {
    envelope.tags = envelope.tags ?? {};
    envelope.tags['ai.cloud.role'] = 'AgentStudio.Web';
    envelope.tags['ai.cloud.roleInstance'] = env.appEnv;
    if (envelope.data?.baseData) {
      envelope.data.baseData.properties = {
        ...(envelope.data.baseData.properties ?? {}),
        appEnv: env.appEnv,
      };
    }
  });

  appInsights.trackPageView();
}

function deriveCorrelationDomains(apiUrl: string): string[] {
  try {
    const u = new URL(apiUrl);
    return [u.host];
  } catch {
    return [];
  }
}

// ── Public helpers ────────────────────────────────────────────────────────────

export function setTelemetryUser(userId: string | number | null, accountId?: string): void {
  if (!appInsights) return;
  if (userId == null) {
    appInsights.clearAuthenticatedUserContext();
  } else {
    appInsights.setAuthenticatedUserContext(String(userId), accountId, true);
  }
}

export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  if (!appInsights) return;
  appInsights.trackEvent({ name }, properties as { [key: string]: string });
}

export function trackException(error: unknown, properties?: Record<string, unknown>): void {
  if (!appInsights) return;
  const err = error instanceof Error ? error : new Error(String(error));
  appInsights.trackException(
    { exception: err },
    properties as { [key: string]: string }
  );
}

export function trackMetric(name: string, average: number, properties?: Record<string, unknown>): void {
  if (!appInsights) return;
  appInsights.trackMetric({ name, average }, properties as { [key: string]: string });
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}
